import { useEffect, useState } from "react";
import { getCurrencies } from "../utils/api";
import { currencyMeta, symbols } from "../utils/currency";

export type CurrencyOption = {
  /** Код в верхнем регистре — в этом виде он хранится в настройках панели. */
  code: string;
  label: string;
  /** Знак для показа рядом с суммой. Пустым не бывает: иначе — код. */
  symbol: string;
  /** Готовая картинка, если она для этой валюты нарисована. */
  img?: string;
};

const fromMeta = (): CurrencyOption[] =>
  Object.entries(currencyMeta).map(([code, meta]) => ({
    code,
    label: meta.label,
    symbol: symbols[code] ?? code,
    img: meta.img,
  }));

// Реестр читаем один раз на загрузку страницы, а не на каждого потребителя:
// хук висит и в переключателе в шапке, и на балансах, и два запроса за одними
// валютами ничем не оправданы. listeners нужны, чтобы заведённая валюта попала
// в уже отрисованный переключатель — он смонтирован в Layout и сам не
// перемонтируется.
let cached: CurrencyOption[] | null = null;
let inflight: Promise<CurrencyOption[] | null> | null = null;
const listeners = new Set<(options: CurrencyOption[]) => void>();

const load = (): Promise<CurrencyOption[] | null> => {
  if (!inflight) {
    inflight = getCurrencies()
      .then((res) => {
        const items: {
          code: string;
          title: string | null;
          symbol?: string;
        }[] = res.data?.items ?? [];
        if (!items.length) return null;

        cached = items.map((item) => {
          const code = item.code.toUpperCase();
          const meta = currencyMeta[code];
          return {
            code,
            label: meta?.label ?? item.title ?? code,
            // Знак берём из реестра: его там и задают. Статическая таблица
            // остаётся запасным вариантом — на бэкенд без поля symbol.
            symbol: item.symbol || symbols[code] || code,
            img: meta?.img,
          };
        });
        return cached;
      })
      .catch(() => {
        // 403 у роли support — ожидаемо, остаёмся на статическом списке.
        // Неудачу не кешируем: при следующем входе роль может быть другой.
        inflight = null;
        return null;
      });
  }
  return inflight;
};

/**
 * Сбрасывает кеш после правки реестра и разносит новый список по тем, кто его
 * показывает. Вызывает раздел валют: заведённая валюта и заданный знак должны
 * появиться сразу, а не после перезагрузки страницы.
 */
export const invalidateCurrencyOptions = () => {
  cached = null;
  inflight = null;
  load().then((loaded) => {
    if (loaded) listeners.forEach((notify) => notify(loaded));
  });
};

/**
 * Список валют для интерфейса: переключатель в шапке, знак рядом с суммой.
 *
 * Источник — реестр /currencies/: валюты заводятся из админки и должны
 * появляться без выката фронта. Нарисованные названия и флаги из currencyMeta
 * остаются в приоритете: они переведены и подобраны вручную, а title из базы —
 * служебный. Знак, наоборот, идёт из реестра: его там и задают.
 *
 * Реестр доступен только роли admin, поэтому для support остаётся прежний
 * статический список — иначе переключатель у поддержки опустел бы.
 */
export const useCurrencyOptions = () => {
  const [options, setOptions] = useState<CurrencyOption[]>(
    () => cached ?? fromMeta(),
  );

  useEffect(() => {
    listeners.add(setOptions);
    if (!cached) {
      load().then((loaded) => {
        if (loaded) setOptions(loaded);
      });
    }
    return () => {
      listeners.delete(setOptions);
    };
  }, []);

  return options;
};

/** Знак выбранной валюты. Если реестр недоступен — статическая таблица. */
export const useCurrencySymbol = (code: string) => {
  const options = useCurrencyOptions();
  const upper = code.toUpperCase();
  return (
    options.find((option) => option.code === upper)?.symbol ??
    symbols[upper] ??
    code
  );
};
